import React, { useState, useRef } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';
import TextareaWithLines from '../components/TextareaWithLines';
import PreWithLines from '../components/PreWithLines';
import {
  parseInputToJavaScriptObject,
  parseInputToJavaScriptObjectSimple,
} from '../utils/jsonSmartParse';

const JsonEscapeUnescapePage = () => {
  const [stringInput, setStringInput] = useState('');
  const [jsonOutput, setJsonOutput] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const lineNumbersRef = useRef(null);
  const outputRef = useRef(null);
  const outputLineNumbersRef = useRef(null);

  const examples = {
    validJson: '{"name":"John","age":30}',
    stringWithEscapedQuotes: '{\\"name\\":\\"John\\",\\"age\\":30}',
    singleQuotes: "{'name':'John','age':30}",
    jsonStringLiteralContainingJson: '"{\\"message\\":\\"hello world\\"}"',
    deeplyEscapedJsonString: '{\"TestProcess\":\"Sms\",\"Data\":\"{\\"Via\\":[2],\\"Id\\":2224433,\\"IsTrue\\":true}\"}',
    doubleEscapedDataField: '{"TestProcess":"Sms","Data":"\\"{\\\\\\"type\\\\\\":\\\\\\"event\\\\\\",\\\\\\"id\\\\\\":123}\\""}',
    plainString: 'This is a "test" string with \n newlines and \\ backslashes.'
  };

  const processStringAndSetOutput = (action) => {
    try {
      setError('');
      setJsonOutput('');
      if (!stringInput.trim() && action !== 'escape') {
        setError("Input is empty. Please enter a string.");
        return;
      }
      if (action === 'escape' && !stringInput) {
        setJsonOutput('""');
        return;
      }
      let outputData;
      if (action === 'escape') {
        outputData = JSON.stringify(stringInput);
      } else if (action === 'simpleUnescapeBeautify' || action === 'simpleUnescapeMinify') {
        // Use simple unescape (1 level only)
        const jsObject = parseInputToJavaScriptObjectSimple(stringInput);
        
        if (action === 'simpleUnescapeMinify') {
          outputData = JSON.stringify(jsObject);
        } else {
          outputData = JSON.stringify(jsObject, null, 2);
        }
        
        // Simple post-processing: if original had \\u0022, restore them
        if (stringInput.includes('\\u0022')) {
          // Replace all escaped quotes with unicode escapes in the entire output
          // This is a simple approach - convert \" to \u0022
          outputData = outputData.replace(/\\"/g, '\\u0022');
        }
      } else {
        // Use deep unescape (existing behavior)
        const jsObject = parseInputToJavaScriptObject(stringInput);
        if (action === 'unescapeMinify') {
          outputData = JSON.stringify(jsObject);
        } else if (action === 'unescapeBeautify') {
          outputData = JSON.stringify(jsObject, null, 2);
        }
      }
      setJsonOutput(outputData);
    } catch (e) {
      setError(`${e.name}: ${e.message}`);
      setJsonOutput('');
    }
  };

  const handleUnescapeAndBeautifyJson = () => processStringAndSetOutput('unescapeBeautify');
  const handleUnescapeAndMinifyJson = () => processStringAndSetOutput('unescapeMinify');
  const handleEscapeStringForJsonContext = () => processStringAndSetOutput('escape');
  
  // New handlers for simple unescape
  const handleSimpleUnescapeAndBeautifyJson = () => processStringAndSetOutput('simpleUnescapeBeautify');
  const handleSimpleUnescapeAndMinifyJson = () => processStringAndSetOutput('simpleUnescapeMinify');

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setStringInput(e.target.result);
        setJsonOutput('');
        setError('');
      };
      reader.readAsText(file);
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const loadExample = (exampleKey) => {
    if (examples[exampleKey]) {
      setStringInput(examples[exampleKey]);
      setJsonOutput('');
      setError('');
    }
  };

  const downloadJsonOutput = () => {
    if (!jsonOutput || !jsonOutput.trim()) {
      setError('Nothing to download. Please process a string first.');
      return;
    }
    try {
      const blob = new Blob([jsonOutput], { type: 'application/json;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'processed_string.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setError('');
    } catch (e) {
      setError('Error downloading JSON: ' + e.message);
    }
  };

  const handleInputScroll = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const handleOutputScroll = () => {
    if (outputRef.current && outputLineNumbersRef.current) {
      outputLineNumbersRef.current.scrollTop = outputRef.current.scrollTop;
    }
  };

  const handleCopyPre = () => {
    if (!jsonOutput) return;
    navigator.clipboard.writeText(jsonOutput).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 1500);
    });
  };

  return (
    <>
      <SEO 
        title="JSON Escape & Unescape Tool" 
        description="Easily escape and unescape JSON strings. Handle various escape formats, nested JSON strings, and convert between different JSON representations."
        keywords={["JSON escape", "JSON unescape", "escape JSON string", "unescape JSON string", "JSON parser", "JSON beautifier", "JSON minifier", "nested JSON", "JSON string manipulation"]}
        canonicalUrl="https://yourjsontools.com/json-escape-unescape"
      />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 20px 0' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>JSON Escape & Unescape</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Escape special characters in JSON strings or unescape encoded JSON. Handle nested JSON and various escape formats.
        </p>
      </div>
      
      <div className="responsive-main-container" style={{
        display: 'flex',
        minHeight: 'calc(100vh - 200px)', 
        maxHeight: '800px', 
        margin: '0 auto',
        padding: '20px',
        gap: '20px', 
        boxSizing: 'border-box',
      }}>
        <div className="responsive-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '400px' }}>
          <TextareaWithLines
            value={stringInput}
            onChange={e => setStringInput(e.target.value)}
            placeholder={"Enter your JSON string here (for escaping or unescaping)"}
            style={{ backgroundColor: '#fff', flexGrow: 1 }}
            textareaRef={textareaRef}
            lineNumbersRef={lineNumbersRef}
            onScroll={handleInputScroll}
            textareaProps={{ className: 'responsive-textarea' }}
            lineNumbersRefClassName="responsive-linenumbers"
          />
        </div>
        <div className="responsive-controls" style={{
          width: '240px',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0',
          boxSizing: 'border-box',
        }}>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} accept=".txt,.json,.js" />
          <button onClick={handleUploadClick} className="control-button" style={{ backgroundColor: '#00BFA5'}}>Upload JSON Data</button>
          <select onChange={(e) => loadExample(e.target.value)} className="control-select" defaultValue="">
            <option value="" disabled>Load Example</option>
            <option value="validJson">Valid JSON Object</option>
            <option value="stringWithEscapedQuotes">String w/ Escaped Quotes</option>
            <option value="singleQuotes">String w/ Single Quotes</option>
            <option value="jsonStringLiteralContainingJson">JSON String Literal (Content is JSON)</option>
            <option value="deeplyEscapedJsonString">Deeply Escaped String (Nested)</option>
            <option value="doubleEscapedDataField">Double Escaped Data Field</option>
            <option value="plainString">Plain String (for Escape)</option>
          </select>
          <button onClick={handleEscapeStringForJsonContext} className="control-button" style={{ backgroundColor: '#fd7e14', fontWeight: 'bold' }}>Escape JSON String</button>
          <button onClick={handleUnescapeAndBeautifyJson} className="control-button" style={{ backgroundColor: '#218838', fontWeight: 'bold' }}>Force Deep Unescape & Beautify</button>
          <button onClick={handleUnescapeAndMinifyJson} className="control-button" style={{ backgroundColor: '#1e7e34' }}>Force Deep Unescape & Minify</button>
          <button onClick={handleSimpleUnescapeAndBeautifyJson} className="control-button" style={{ backgroundColor: '#6f42c1', fontWeight: 'bold' }}>Simple Unescape & Beautify</button>
          <button onClick={handleSimpleUnescapeAndMinifyJson} className="control-button" style={{ backgroundColor: '#563d7c' }}>Simple Unescape & Minify</button>
          <button onClick={downloadJsonOutput} className="control-button" style={{ backgroundColor: '#5a6268' }}>Download Output</button>
          {error && (
            <div style={{ padding: '10px', marginTop: '10px', color: 'white', backgroundColor: '#dc3545', textAlign: 'left', width: '100%', fontWeight: 'normal', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box', wordBreak: 'break-word', lineHeight: '1.4' }}>
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>
        <div className="responsive-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: '400px' }}>
          <div style={{ position: 'relative', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            {jsonOutput && (
              <button onClick={handleCopyPre} className="copy-button">
                {copySuccess ? 'Copied!' : 'Copy'}
              </button>
            )}
            <TextareaWithLines
              value={jsonOutput}
              onChange={() => {}} // No-op since it's readonly
              placeholder="Output will appear here..."
              style={{ backgroundColor: '#fafafa', flexGrow: 1 }}
              textareaRef={outputRef}
              lineNumbersRef={outputLineNumbersRef}
              onScroll={handleOutputScroll}
              textareaProps={{ 
                className: 'responsive-pre',
                readOnly: true,
                style: { 
                  fontFamily: 'Consolas, Monaco, monospace',
                  fontSize: '14px',
                  whiteSpace: 'pre',
                  wordBreak: 'break-all'
                }
              }}
              lineNumbersRefClassName="responsive-linenumbers"
            />
          </div>
        </div>
      </div>
      <style jsx global>{`
        .responsive-textarea, .responsive-pre {
            height: 100% !important; 
            flex-grow: 1;
        }
        .control-button {
          width: 100%;
          padding: 10px;
          margin: 5px 0; 
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: background-color 0.2s ease, opacity 0.2s ease;
        }
        .control-button:hover {
          opacity: 0.85;
        }
        .control-select {
          width: 100%;
          padding: 10px;
          margin: 5px 0;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
          box-sizing: border-box;
          background-color: white;
        }
        .copy-button {
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 10;
          padding: 6px 12px;
          background-color: ${copySuccess ? '#4CAF50' : '#00BFA5'};
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 500;
          transition: background-color 0.3s;
        }
        .copy-button:hover {
            opacity: 0.85;
        }

        /* Fix for output panel line numbers */
        .responsive-panel:last-child .line-numbered-textarea {
          position: relative;
        }
        
        .responsive-panel:last-child .line-numbers {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 5;
          background-color: #f8f9fa;
          border-right: 1px solid #dee2e6;
          padding: 10px 8px;
          font-family: Consolas, Monaco, monospace;
          font-size: 14px;
          line-height: 1.4;
          color: #6c757d;
          pointer-events: none;
          user-select: none;
          overflow: hidden;
        }
        
        .responsive-panel:last-child textarea {
          padding-left: 50px !important;
          background-color: #fafafa !important;
        }

        @media (max-width: 992px) { 
          .responsive-main-container {
            flex-direction: column;
            height: auto; 
            max-height: none;
            gap: 20px;
          }
          .responsive-controls {
            width: 100%;
            order: 1; 
            margin-bottom: 20px;
          }
          .responsive-panel {
            min-height: 300px; 
            width: 100%; 
          }
           .responsive-panel:nth-child(1) { order: 0; }
           .responsive-panel:nth-child(3) { order: 2; }
        }
      `}</style>
      <div style={{ maxWidth: '1200px', margin: '40px auto 20px', padding: '0 20px', fontSize: '15px', lineHeight: '1.6', color: '#444' }}>
        <h2 style={{ fontSize: '22px', marginBottom: '15px', color: '#333' }}>About JSON Escape & Unescape Tool</h2>
        <p style={{ marginBottom: '15px' }}>
          This tool makes it easy to escape and unescape JSON strings. Whether you need to prepare strings for JSON inclusion or decode escaped JSON strings, this tool handles various formats and levels of escaping.
        </p>
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>How It Works:</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
            <li style={{ marginBottom: '8px' }}><strong>JSON Escaping:</strong> Turns plain text into a JSON-safe string by properly escaping special characters and control characters.</li>
            <li style={{ marginBottom: '8px' }}><strong>Simple JSON Unescaping:</strong> Performs only one level of unescaping. Best for most common cases where you have a single-escaped JSON string.</li>
            <li style={{ marginBottom: '8px' }}><strong>Force Deep JSON Unescaping:</strong> The tool intelligently detects and unescapes JSON strings through multiple levels:
                <ul style={{paddingLeft: '20px', marginTop:'5px'}}>
                    <li style={{marginBottom:'4px'}}>Converting escaped sequences (like <code>\"</code>, <code>\\n</code>, <code>\\u2022</code>) to their actual characters.</li>
                    <li style={{marginBottom:'4px'}}>Handling single quotes as substitutes for double quotes when needed.</li>
                    <li style={{marginBottom:'4px'}}>Supporting multiple layers of escaping in nested JSON strings.</li>
                    <li style={{marginBottom:'4px'}}>Recursively inspects and unescapes string values within JSON structures.</li>
                </ul>
            </li>
        </ul>
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Use Cases:</h3>
         <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}>Preparing strings for inclusion in JSON documents.</li>
          <li style={{ marginBottom: '8px' }}>Extracting readable content from escaped JSON strings.</li>
          <li style={{ marginBottom: '8px' }}><strong>Simple Unescape:</strong> Use when you have standard escaped JSON strings and want predictable, single-level unescaping.</li>
          <li style={{ marginBottom: '8px' }}><strong>Force Deep Unescape:</strong> Use for deeply escaped strings where JSON is embedded within JSON (nested escaping).</li>
          <li style={{ marginBottom: '8px' }}>Converting between different JSON string formats and representations.</li>
        </ul>
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Escape Sequences Handled:</h3>
        <p style={{ marginBottom: '10px' }}>
          The tool handles all standard JSON escape sequences:
        </p>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '4px' }}><code>\"</code> - Double quotes</li>
          <li style={{ marginBottom: '4px' }}><code>\\</code> - Backslash</li>
          <li style={{ marginBottom: '4px' }}><code>\/</code> - Forward slash</li>
          <li style={{ marginBottom: '4px' }}><code>\b</code> - Backspace</li>
          <li style={{ marginBottom: '4px' }}><code>\f</code> - Form feed</li>
          <li style={{ marginBottom: '4px' }}><code>\n</code> - New line</li>
          <li style={{ marginBottom: '4px' }}><code>\r</code> - Carriage return</li>
          <li style={{ marginBottom: '4px' }}><code>\t</code> - Tab</li>
          <li style={{ marginBottom: '4px' }}><code>\uXXXX</code> - Unicode characters</li>
        </ul>
        <h3 style={{ fontSize: '18px', margin: '20px 0 10px', color: '#333' }}>Key Features:</h3>
        <ul style={{ marginBottom: '15px', paddingLeft: '20px' }}>
          <li style={{ marginBottom: '8px' }}><strong>Two Unescape Modes:</strong> Simple (1-level) and Force Deep (multi-level) unescaping options</li>
          <li style={{ marginBottom: '8px' }}><strong>Advanced Multi-Layer Escaping/Unescaping</strong></li>
          <li style={{ marginBottom: '8px' }}><strong>Handles Nested JSON Strings</strong></li>
          <li style={{ marginBottom: '8px' }}><strong>Single-Quote Support</strong></li>
          <li style={{ marginBottom: '8px' }}>Flexible Output Formatting (Beautify with custom indent or Minify)</li>
          <li style={{ marginBottom: '8px' }}>Client-Side Processing: All operations happen in your browser for data privacy</li>
        </ul>
        <p>
          This comprehensive tool ensures that even complex and multi-layered JSON strings can be properly escaped or unescaped. Choose Simple Unescape for standard cases or Force Deep Unescape for complex nested structures.
        </p>
      </div>
    </>
  );
};

export default JsonEscapeUnescapePage;
